import { useCallback, useEffect, useState } from "react";
import { ListFilter, RefreshCw, Save } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtBytes, fmtNum } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { ConfirmDialog } from "@/components/ConfirmDialog";

export default function IndexPanel({ db, index, onChange }) {
  const [fields, setFields] = useState((index?.fields || []).join(", "));
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const rx = index?.reindex;
  const running = !!rx?.running;

  useEffect(() => { setFields((index?.fields || []).join(", ")); }, [index?.fields]);

  const poll = useCallback(() => { if (running) { const t = setTimeout(onChange, 1500); return () => clearTimeout(t); } }, [running, onChange]);
  useEffect(poll, [poll]);

  const saveFields = async () => {
    setBusy(true);
    try {
      const list = fields.split(",").map((s) => s.trim()).filter(Boolean);
      await api.put(`/databases/${db}/index`, { fields: list });
      toast.success(list.length ? `Indexando: ${list.join(", ")}` : "Índice desligado: nenhum campo será indexado");
      onChange?.();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const reindex = async () => {
    setConfirm(false);
    try {
      await api.post(`/databases/${db}/reindex`);
      toast.success("Reindexação iniciada");
      onChange?.();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const pct = rx?.total ? Math.min(100, Math.round((rx.done / rx.total) * 100)) : 0;

  return (
    <div className="space-y-6" data-testid="index-panel">
      <div className="grid sm:grid-cols-3 border border-zinc-800 divide-y sm:divide-y-0 sm:divide-x divide-zinc-800 bg-zinc-900/60 rounded-sm">
        <div className="p-5"><div className="overline-label">Entradas (aprox.)</div><div className="font-display text-2xl font-bold mt-2" data-testid="index-entries">{fmtNum(index?.entries_approx || 0)}</div></div>
        <div className="p-5"><div className="overline-label">Disco do índice</div><div className="font-display text-2xl font-bold mt-2" data-testid="index-bytes">{fmtBytes(index?.bytes || 0)}</div></div>
        <div className="p-5"><div className="overline-label">Campos</div><div className="font-display text-2xl font-bold mt-2 truncate" data-testid="index-fields-summary">{index?.fields?.length ? `${index.fields.length} selecionados` : "Desligado"}</div></div>
      </div>

      <div className="panel p-5 space-y-3">
        <div className="flex items-center gap-2"><ListFilter className="h-4 w-4 text-orange-500" /><div className="font-display font-semibold">Campos indexados</div></div>
        <p className="text-sm text-zinc-400">Os campos listados aqui são indexados automaticamente por palavra (sem acento, sem maiúsculas) a cada gravação, permitindo buscas com <code className="font-mono text-zinc-200">?where=campo:valor</code>. Deixe vazio para <b>desligar</b> o índice (só leitura por chave, ingestão mais rápida e menos disco), ou liste os campos que você vai pesquisar (ex.: <code className="font-mono text-zinc-200">cpf, pai, mae, rgNumero, cns, telefone</code>).</p>
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="flex-1 space-y-1">
            <Label htmlFor="index-fields">Lista separada por vírgula (vazio = índice desligado)</Label>
            <Input id="index-fields" value={fields} onChange={(e) => setFields(e.target.value)} placeholder="nenhum campo (índice desligado)" data-testid="index-fields-input" className="bg-transparent border-zinc-800 rounded-sm font-mono" />
          </div>
          <Button onClick={saveFields} disabled={busy} data-testid="index-fields-save" className="sm:self-end bg-orange-600 hover:bg-orange-500 text-white rounded-sm btn-tactile"><Save className="h-4 w-4 mr-1" /> Salvar</Button>
        </div>
        <p className="text-xs text-zinc-500">A configuração vale para escritas futuras. Para aplicar ao que já existe, reindexe abaixo.</p>
      </div>

      <div className="panel p-5 space-y-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="font-display font-semibold">Reindexar</div>
            <p className="text-sm text-zinc-400">Apaga o índice e reconstrói a partir de todos os registros do banco. Use após mudar os campos ou após importações feitas antes do índice existir.</p>
          </div>
          <Button variant="outline" disabled={running} onClick={() => setConfirm(true)} data-testid="reindex-button" className="rounded-sm border-zinc-700 btn-tactile"><RefreshCw className={`h-4 w-4 mr-1 ${running ? "animate-spin" : ""}`} /> {running ? "Reindexando…" : "Reindexar"}</Button>
        </div>
        {rx?.started_at && (
          <div className="space-y-2" data-testid="reindex-status">
            <Progress value={pct} className="h-1.5 bg-zinc-800" />
            <div className="text-xs text-zinc-500 font-mono flex justify-between">
              <span>{fmtNum(rx.done)} / {fmtNum(rx.total)} registros ({pct}%)</span>
              <span>{rx.error ? <span className="text-red-400">erro: {rx.error}</span> : running ? "em andamento" : `concluído em ${rx.finished_at?.replace("T", " ").replace("Z", "")}`}</span>
            </div>
          </div>
        )}
      </div>

      <ConfirmDialog open={confirm} onOpenChange={setConfirm} title="Reindexar este banco?" description="O índice atual será apagado e reconstruído. Buscas por campo podem devolver resultados parciais até terminar; leituras por chave não são afetadas." confirmLabel="Reindexar" onConfirm={reindex} testId="confirm-reindex" />
    </div>
  );
}
